import { Tooltip } from 'antd';
import { useEffect, useState } from 'react';

import Tab, { TabItem } from '@/components/common/Tab';
import { UseTranslateType } from '@/hooks/useTranslate';
import { EventNewID } from '@/utils/eventlog/eventNewConfig';
import { trackEvent } from '@/utils/eventlog/logAdapter';

import CommonModal from '..';
import CloseIcon from '../icon/close';
import BenefitComponent from './components/benefit';
import InviteComponent from './components/invite';
import { useDistributionCode } from './hooks/useDistributionCode';

type TabActiveKey = 'invite' | 'benefit';

interface DistributionModalProps {
  onClose?: () => void;
}

interface DistributionContentProps extends DistributionModalProps {
  t: UseTranslateType;
}

interface TabContentProps {
  activeKey: TabActiveKey;
  t: UseTranslateType;
  onChange: (key: TabActiveKey) => void;
}

const TabContent = ({ t, activeKey, onChange }: TabContentProps) => {
  if (!t) {
    return null;
  }

  const items: TabItem<TabActiveKey>[] = [
    {
      key: 'invite',
      label: (activeKey) => {
        return (
          <span
            className={activeKey === 'invite' ? 'text-app-primary' : 'text-app-secondary'}
          >
            {t('Distribution-Invite')}
          </span>
        );
      },
    },
    {
      key: 'benefit',
      label: (activeKey) => {
        return (
          <span
            className={
              activeKey === 'benefit' ? 'text-app-primary' : 'text-app-secondary'
            }
          >
            {t('Distribution-Earnings')}
          </span>
        );
      },
    },
  ];

  return (
    <div className="flex justify-center">
      <Tab
        className="gap-[20px]"
        itemClassName="text-[16px] font-[600] leading-[22px]"
        sliderClassName="!h-[2px] bg-[#5661F1] bottom-[-4px]"
        type="slider"
        sliderType="underline"
        items={items}
        activeKey={activeKey}
        onChange={onChange}
      />
    </div>
  );
};

const InfoIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="10" cy="10" r="9" stroke="#6366f1" strokeWidth="1.5" fill="none" />
    <text
      x="10"
      y="14.5"
      textAnchor="middle"
      fill="#6366f1"
      fontSize="12"
      fontWeight="600"
      fontFamily="sans-serif"
    >
      i
    </text>
  </svg>
);

const NormalUserTooltip = ({ t }: { t: UseTranslateType }) => (
  <div className="p-[4px]">
    <p className="font-[600] text-[13px] mb-[4px]">
      {t('Distribution-Tooltip-Normal-Title')}
    </p>
    <ul className="list-disc pl-[16px] text-[12px] leading-[18px] m-0">
      <li>{t('Distribution-Tooltip-Normal-Rule1')}</li>
      <li>{t('Distribution-Tooltip-Normal-Rule2')}</li>
    </ul>
  </div>
);

const KOLUserTooltip = ({ t }: { t: UseTranslateType }) => (
  <div className="p-[4px]">
    <p className="font-[600] text-[13px] mb-[4px]">
      {t('Distribution-Tooltip-KOL-Title')}
    </p>
    <ul className="list-disc pl-[16px] text-[12px] leading-[18px] m-0">
      <li>{t('Distribution-Tooltip-KOL-Rule1')}</li>
      <li>{t('Distribution-Tooltip-KOL-Rule2')}</li>
      <li>{t('Distribution-Tooltip-KOL-Rule3')}</li>
    </ul>
  </div>
);

const Content = ({ t, onClose }: DistributionContentProps) => {
  const [activeKey, setActiveKey] = useState<TabActiveKey>('invite');
  const distributionCode = useDistributionCode();
  const isKOL = Boolean(distributionCode.inviteInfo?.pattern === 2);

  useEffect(() => {
    trackEvent(EventNewID.INVITE_ENTRY_VIEW, {} as Record<string, never>);
  }, []);

  const handleTabChange = (key: TabActiveKey) => {
    setActiveKey(key);
  };

  return (
    <div className="flex flex-col bg-app-panel border border-app rounded-[8px]">
      <div className="pt-[15px] pb-[19px] pl-[32px] pr-[18px] flex justify-between items-center">
        <TabContent activeKey={activeKey} t={t} onChange={handleTabChange} />
        <div className="flex items-center gap-[8px]">
          <Tooltip
            title={isKOL ? <KOLUserTooltip t={t} /> : <NormalUserTooltip t={t} />}
            placement="bottomRight"
            color="var(--app-bg-popover)"
            styles={{
              body: {
                color: 'var(--app-text-primary)',
                maxWidth: 320,
                border: '1px solid var(--app-border)',
              },
            }}
          >
            <div className="cursor-pointer flex items-center w-[16px] h-[16px]">
              <InfoIcon />
            </div>
          </Tooltip>
          <div className="cursor-pointer" onClick={onClose}>
            <CloseIcon />
          </div>
        </div>
      </div>
      <div className="min-h-[240px]">
        {activeKey === 'invite' ? (
          <InviteComponent distributionCode={distributionCode} />
        ) : (
          <BenefitComponent isKOL={isKOL} />
        )}
      </div>
    </div>
  );
};

export function distributionModalShow(options: DistributionModalProps) {
  CommonModal.show((t) => ({
    width: 518,
    customPadding: {
      x: 0,
      y: 0,
    },
    footer: false,
    modalClassName: 'distribution-modal',
    content: (close) => (
      <Content
        t={t}
        onClose={() => {
          close();
          options.onClose?.();
        }}
      />
    ),
  }));
}
